/**
 * Destination-matched campaign hotels shown first in hotel search results.
 * Add a row here when launching a new allotment campaign (Belvedere, Royal Tulip, …).
 *
 * Flags:
 * - kosher: show «כשר» on search/profile badges (only when true)
 * - skipInventoryHold: skip campaign room-inventory API (holds / stop-sale)
 */
import { BELVEDERE_HERO_IMAGE } from "./belvedereHotelMedia";
import { belvedereContract } from "./belvedereContractData";
import { ROYAL_TULIP_HERO_IMAGE } from "./royalTulipTbilisiHotelMedia";
import { royalTulipTbilisiContract } from "./royalTulipTbilisiContractData";
import { royalZanzibarBeachResortContract } from "./royalZanzibarBeachResortContractData";
import { ROYAL_ZANZIBAR_HERO_IMAGE } from "./royalZanzibarBeachResortHotelMedia";
import {
  buildOpsContractFromHotel,
  campaignHotelCodesAreAliases,
  getOverlayHotel,
  mergeContractWithOverlayRooms,
  mergeDestinationCampaignHotels,
} from "./campaignHotelOpsOverlay";

export const CAMPAIGN_HOTEL_SPECIAL_PRICES_LABEL_HE = "מחירים מיוחדים";
export const CAMPAIGN_HOTEL_KOSHER_BADGE_HE = "כשר";
export const CAMPAIGN_HOTEL_SPECIAL_PRICES_BADGE_HE = `${CAMPAIGN_HOTEL_SPECIAL_PRICES_LABEL_HE} · ${CAMPAIGN_HOTEL_KOSHER_BADGE_HE}`;

export function campaignHotelIsKosher(campaignOrPkg) {
  if (!campaignOrPkg || typeof campaignOrPkg !== "object") return false;
  if (campaignOrPkg.kosher === true || campaignOrPkg.campaignKosher === true) return true;
  return false;
}

export function campaignHotelBadgeText(campaignOrPkg) {
  if (campaignHotelIsKosher(campaignOrPkg)) return CAMPAIGN_HOTEL_SPECIAL_PRICES_BADGE_HE;
  return CAMPAIGN_HOTEL_SPECIAL_PRICES_LABEL_HE;
}

function campaignRow({
  contract,
  quoteKind,
  destinationCodes,
  destinationNameHints,
  duplicateNamePattern,
  slug,
  publicPath,
  image,
  skipInventoryHold = false,
}) {
  const kosher = Boolean(contract?.kosher);
  return {
    id: `campaign-${slug}`,
    destinationCodes,
    destinationNameHints,
    duplicateNamePattern,
    slug,
    publicPath,
    displayName: contract.hotelName,
    address: contract.locationHe,
    starRating: contract.starRating,
    image,
    currency: contract.currency,
    quoteKind,
    hotelCode: contract.hotelCode,
    kosher,
    skipInventoryHold: Boolean(skipInventoryHold),
    badgeText: campaignHotelBadgeText({ kosher }),
  };
}

export const DESTINATION_CAMPAIGN_HOTELS = [
  campaignRow({
    contract: royalZanzibarBeachResortContract,
    quoteKind: "royalZanzibar",
    destinationCodes: ["ZNZ"],
    destinationNameHints: [
      "zanzibar",
      "nungwi",
      "tanzania",
      "זנזיבר",
      "זנזibar",
      "נונגווי",
      "טנזניה",
    ],
    duplicateNamePattern: /royal\s*zanzibar/i,
    slug: "royal-zanzibar-beach-resort-2026",
    publicPath: "/he/campaigns/royal-zanzibar-beach-resort-2026",
    image: ROYAL_ZANZIBAR_HERO_IMAGE,
    // Still managed in admin inventory; checkout skips soft-holds via isRoyalZanzibarCampaignCode.
    skipInventoryHold: false,
  }),
  campaignRow({
    contract: royalTulipTbilisiContract,
    quoteKind: "royalTulip",
    destinationCodes: ["TBS"],
    destinationNameHints: ["tbilisi", "טביליסי", "georgia", "גאורגיה"],
    duplicateNamePattern: /royal\s*tulip/i,
    slug: "royal-tulip-tbilisi-2026",
    publicPath: "/he/campaigns/royal-tulip-tbilisi-2026",
    image: ROYAL_TULIP_HERO_IMAGE,
    skipInventoryHold: false,
  }),
  campaignRow({
    contract: belvedereContract,
    quoteKind: "belvedere",
    destinationCodes: ["HER"],
    destinationNameHints: [
      "crete",
      "heraklion",
      "hersonissos",
      "herssonisos",
      "כרתים",
      "חרסוניסוס",
      "הרקליון",
    ],
    duplicateNamePattern: /belvedere/i,
    slug: "royal-belvedere-2026",
    publicPath: "/he/campaigns/royal-belvedere-2026",
    image: BELVEDERE_HERO_IMAGE,
    skipInventoryHold: false,
  }),
];

/** Built-in registry plus overlay hotels (server hydrate + local cache). */
export function getDestinationCampaignHotels() {
  return mergeDestinationCampaignHotels(DESTINATION_CAMPAIGN_HOTELS);
}

export function getDestinationCampaignHotelByCode(hotelCode) {
  const code = String(hotelCode || "").trim();
  if (!code) return null;
  return (
    getDestinationCampaignHotels().find(
      (row) =>
        row.hotelCode === code ||
        row.slug === code ||
        campaignHotelCodesAreAliases(row.hotelCode, code),
    ) || null
  );
}

/** IATA destination for campaign flight search (HER, TBS, …). */
export function resolveCampaignFlightDestination(campaignOrCode) {
  const code =
    typeof campaignOrCode === "string"
      ? String(campaignOrCode || "").trim()
      : String(campaignOrCode?.hotelCode || campaignOrCode?.slug || "").trim();
  const row =
    getDestinationCampaignHotelByCode(code) ||
    (typeof campaignOrCode === "object" &&
    campaignOrCode &&
    Array.isArray(campaignOrCode.destinationCodes)
      ? campaignOrCode
      : null);
  const dest = Array.isArray(row?.destinationCodes) ? row.destinationCodes[0] : null;
  const normalized = String(dest || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : null;
}

export function inventoryManagedCampaignHotels() {
  return getDestinationCampaignHotels().filter((row) => !row.skipInventoryHold);
}

export function resolveCampaignContract(campaignOrCode) {
  const code =
    typeof campaignOrCode === "string"
      ? String(campaignOrCode || "").trim()
      : String(campaignOrCode?.hotelCode || campaignOrCode?.slug || "").trim();
  const quoteKind =
    typeof campaignOrCode === "object" && campaignOrCode ? campaignOrCode.quoteKind : null;
  if (
    quoteKind === "royalZanzibar" ||
    code === royalZanzibarBeachResortContract.hotelCode ||
    campaignHotelCodesAreAliases(code, royalZanzibarBeachResortContract.hotelCode)
  ) {
    return mergeContractWithOverlayRooms(
      royalZanzibarBeachResortContract,
      royalZanzibarBeachResortContract.hotelCode,
    );
  }

  const overlayHotel = getOverlayHotel(code);
  if (overlayHotel) return buildOpsContractFromHotel(overlayHotel);

  const byCode = getDestinationCampaignHotelByCode(code);
  if (quoteKind === "royalTulip" || code === royalTulipTbilisiContract.hotelCode || byCode?.quoteKind === "royalTulip") {
    return mergeContractWithOverlayRooms(royalTulipTbilisiContract, code);
  }
  if (quoteKind === "belvedere" || code === belvedereContract.hotelCode || byCode?.quoteKind === "belvedere") {
    return mergeContractWithOverlayRooms(belvedereContract, code);
  }
  return null;
}

export function campaignInventorySeasonRange(campaign) {
  const contract = resolveCampaignContract(campaign);
  const seasons = Array.isArray(contract?.seasons) ? contract.seasons : [];
  const froms = seasons.map((row) => String(row?.from || "").slice(0, 10)).filter(Boolean).sort();
  const tos = seasons.map((row) => String(row?.to || "").slice(0, 10)).filter(Boolean).sort();
  return {
    start: froms[0] || null,
    end: tos[tos.length - 1] || null,
  };
}
